// src/utils/pushNotifications.js
// Frontend utility for managing push notification subscriptions

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Check if push notifications are supported by this browser
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Check if notifications are permitted
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted', 'denied', or 'default'
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  // Request permission
  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Register service worker if not already registered
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('[Push] Service workers not supported');
    return null;
  }

  try {
    // Check if already registered
    let registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
      console.log('[Push] Registering service worker...');
      registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      console.log('[Push] Service worker registered');
    }

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    console.log('[Push] Service worker ready');

    return registration;
  } catch (error) {
    console.error('[Push] Service worker registration failed:', error);
    return null;
  }
}

/**
 * Get VAPID public key from server
 */
async function getVapidPublicKey() {
  try {
    const response = await fetch(`${API_BASE}/push/vapid-key`, {
      credentials: 'include'
    });

    if (!response.ok) {
      console.error('[Push] Failed to get VAPID key');
      return null;
    }

    const data = await response.json();
    return data.ok ? data.publicKey : null;
  } catch (error) {
    console.error('[Push] Error fetching VAPID key:', error);
    return null;
  }
}

/**
 * Convert VAPID key to Uint8Array for subscription
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Subscribe to push notifications
 * @param {string} deviceName - Optional name for this device
 */
export async function subscribeToPush(deviceName = null) {
  try {
    // Check support
    if (!isPushSupported()) {
      return { success: false, error: 'Push notifications not supported' };
    }

    // Get permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      return { success: false, error: 'Notification permission denied' };
    }

    // Register service worker
    const registration = await registerServiceWorker();
    if (!registration) {
      return { success: false, error: 'Failed to register service worker' };
    }

    // Get VAPID key from server
    const vapidKey = await getVapidPublicKey();
    if (!vapidKey) {
      return { success: false, error: 'Push notifications not configured on server' };
    }

    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription();

    // Create new subscription if needed
    if (!subscription) {
      console.log('[Push] Creating new push subscription...');
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
      console.log('[Push] Push subscription created');
    }

    // Send subscription to server
    const response = await fetch(`${API_BASE}/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        deviceName: deviceName || getDeviceName()
      })
    });

    const responseData = await response.json().catch(() => null);

    if (!response.ok) {
      const errorMsg = responseData?.error || `Server error ${response.status}`;
      console.error('[Push] Subscribe failed:', errorMsg, responseData);
      throw new Error(errorMsg);
    }

    console.log('[Push] Subscription saved to server');
    return { success: true, subscription };
  } catch (error) {
    console.error('[Push] Subscribe error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return { success: true }; // No registration = already unsubscribed
    }

    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      // Tell server to remove subscription
      await fetch(`${API_BASE}/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      });

      // Unsubscribe locally
      await subscription.unsubscribe();
      console.log('[Push] Unsubscribed successfully');
    }

    return { success: true };
  } catch (error) {
    console.error('[Push] Unsubscribe error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if user is currently subscribed to push
 */
export async function isSubscribedToPush() {
  try {
    if (!isPushSupported()) {
      return false;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return false;
    }

    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    console.error('[Push] Error checking subscription:', error);
    return false;
  }
}

/**
 * Get push subscription status from server
 */
export async function getPushStatus() {
  try {
    // First check if push is configured (this endpoint doesn't require auth)
    const vapidResponse = await fetch(`${API_BASE}/push/vapid-key`, {
      credentials: 'include'
    });

    let pushConfigured = false;
    if (vapidResponse.ok) {
      const vapidData = await vapidResponse.json();
      pushConfigured = vapidData.ok && !!vapidData.publicKey;
    }

    // Then get user's subscription status (requires auth)
    const response = await fetch(`${API_BASE}/push/status`, {
      credentials: 'include'
    });

    if (!response.ok) {
      // Even if status fails (auth issue), we can still return push configured status
      return {
        ok: true,
        pushConfigured,
        subscribed: false,
        devices: []
      };
    }

    const data = await response.json();
    return data.ok ? { ...data, pushConfigured } : { ok: true, pushConfigured, subscribed: false, devices: [] };
  } catch (error) {
    console.error('[Push] Error getting push status:', error);
    return null;
  }
}

/**
 * Send a test notification
 */
export async function sendTestNotification() {
  try {
    const response = await fetch(`${API_BASE}/push/test`, {
      method: 'POST',
      credentials: 'include'
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[Push] Test notification error:', error);
    return { ok: false, error: error.message };
  }
}

/**
 * Get/update notification preferences
 */
export async function getNotificationPreferences() {
  try {
    const response = await fetch(`${API_BASE}/push/preferences`, {
      credentials: 'include'
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.ok ? data.preferences : null;
  } catch (error) {
    console.error('[Push] Error getting preferences:', error);
    return null;
  }
}

export async function updateNotificationPreferences(preferences) {
  try {
    const response = await fetch(`${API_BASE}/push/preferences`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(preferences)
    });

    const data = await response.json();
    return data.ok;
  } catch (error) {
    console.error('[Push] Error updating preferences:', error);
    return false;
  }
}

/**
 * Generate a device name based on browser/OS
 */
function getDeviceName() {
  const ua = navigator.userAgent;
  let device = 'Unknown Device';

  if (/iPhone/.test(ua)) {
    device = 'iPhone';
  } else if (/iPad/.test(ua)) {
    device = 'iPad';
  } else if (/Android/.test(ua)) {
    device = 'Android';
  } else if (/Windows/.test(ua)) {
    device = 'Windows';
  } else if (/Mac/.test(ua)) {
    device = 'Mac';
  } else if (/Linux/.test(ua)) {
    device = 'Linux';
  }

  // Add browser
  if (/Chrome/.test(ua) && !/Edge/.test(ua)) {
    device += ' Chrome';
  } else if (/Safari/.test(ua) && !/Chrome/.test(ua)) {
    device += ' Safari';
  } else if (/Firefox/.test(ua)) {
    device += ' Firefox';
  } else if (/Edge/.test(ua)) {
    device += ' Edge';
  }

  return device;
}
